import { Input, Stack, vars } from '@etg/wings';
import { ConditionalWrapper } from '@eti/components';
import { Checkbox } from '@eti/form';
import { useProperty, useTranslation } from '@eti/providers';
import type { FareRule, PriceFormat } from '@eti/schema-types';
import { css } from '@eti/styles';
import type { MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import type { FormErrors, FormStateMap } from 'redux-form';
import { getFormSyncErrors, hasSubmitFailed } from 'redux-form';
import { useJson } from '../../../../common/custom-hooks/json';
import { endpoints } from '../../../../constants/routesAndApisConstants';
import { PAYMENT } from '../../../../constants/formNamesConstants';
import { PAYMENT_CONDITIONS } from '../../constants/formFields';
import { getCarrierConditionsLinks } from '../../utils/carriers';
import type { BoundsType } from '../../models';
import TravelConditionsText from './TravelConditionsText';

const checkboxStyles = css`
  margin-inline-end: 8px;
`;

const labelStyles = css`
  display: flex;
  max-width: 530px;
`;

const stackStyles = css`
  border: 1px solid ${vars.colors.signal.critical.light};
  border-radius: 4px;
  padding: 16px 8px;
`;

interface PaymentFormState {
  [PAYMENT_CONDITIONS]?: boolean;
  [key: string]: any; // escape-hatch
}

interface ConnectedProps {
  bounds: BoundsType;
  fareRules: FareRule[];
  isOtherChargeCurrency?: boolean;
}

interface ConnectedState {
  isError: boolean;
}

const mapStateToProps: MapStateToProps<ConnectedState, ConnectedProps, { form: FormStateMap }> = (
  state,
) => {
  const formErrors: FormErrors<PaymentFormState, any> = getFormSyncErrors(PAYMENT)(state);
  const isError = Boolean(formErrors[PAYMENT_CONDITIONS]) && hasSubmitFailed(PAYMENT)(state);

  return { isError };
};

interface PaymentConditionsProps {
  bounds: BoundsType;
  chargeCurrencyPriceFormat?: PriceFormat;
  fareRules: FareRule[];
  isError: boolean;
  isOtherChargeCurrency?: boolean;
  priceInChargeCurrency?: number;
}

const PaymentConditions = ({
  bounds,
  chargeCurrencyPriceFormat,
  fareRules,
  isError,
  isOtherChargeCurrency = false,
  priceInChargeCurrency,
}: PaymentConditionsProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { data } = useJson(endpoints.PAYMENT_GET_TEXTS);
  const { model: texts = {} } = data || {};

  const showProminentValidationTextBorder = p('Payment.ProminentValidationTextBorder.Enabled');
  const lccConditionsLinks = getCarrierConditionsLinks(bounds);

  return (
    <ConditionalWrapper
      condition={showProminentValidationTextBorder}
      wrapper={(children) => (
        <Stack className={isError ? stackStyles : undefined}>{children}</Stack>
      )}
    >
      <>
        <Input.Meta
          error={t('ValidationMessages.TermsAndConditionsNotChecked')}
          errorId="conditions-error"
          hasError={isError}
        />
        {/* eslint-disable jsx-a11y/label-has-associated-control */}
        <label className={labelStyles} htmlFor="conditions">
          <Checkbox
            aria-errormessage="conditions-error"
            className={checkboxStyles}
            data-testid={`${PAYMENT_CONDITIONS}-input`}
            hasError={isError}
            id="conditions"
            name={PAYMENT_CONDITIONS}
            required
            validationRuleName={PAYMENT_CONDITIONS}
          />
          <TravelConditionsText
            bounds={bounds}
            chargeCurrencyPriceFormat={chargeCurrencyPriceFormat}
            fareRules={fareRules}
            fareRulesDescription={texts.fareRulesDescription}
            isOtherChargeCurrency={isOtherChargeCurrency}
            lccConditionsLinks={lccConditionsLinks}
            priceInChargeCurrency={priceInChargeCurrency}
          />
        </label>
      </>
    </ConditionalWrapper>
  );
};

export default connect(mapStateToProps)(PaymentConditions);
